import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import MobileNav from './MobileNav';
import CommandPalette from '@/components/common/CommandPalette';
import SetupFab from '@/components/setup/SetupFab';
import api from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';

// When a new tenant first signs in they have no bot connected. We check the
// onboarding status once per session and push them to /onboarding if they
// haven't completed the wizard. Skipping the check:
//   - on /onboarding itself (obviously)
//   - on /settings (so they can add channel creds manually)
//   - for a one-shot localStorage flag that marks the session as "checked"
//     so we don't re-fetch on every Outlet swap.
const ONBOARD_FLAG = 'onboarding_checked_v1';

// Shown while a platform admin is inside another tenant's workspace via an
// impersonation session (1h token). Impossible to miss on purpose.
function ImpersonationBanner() {
  const { isImpersonating, endImpersonation } = useAuth();
  const { tenant } = useTenant();
  if (!isImpersonating) return null;
  return (
    <div className="sticky top-0 z-30 flex items-center justify-between gap-3 px-4 py-1.5 bg-warn/15 border-b border-warn/40 text-xs">
      <span className="text-text">
        Viewing <strong>{tenant?.name ?? 'tenant'}</strong> as platform admin — actions are audited under your admin account.
      </span>
      <button
        onClick={endImpersonation}
        className="shrink-0 px-2.5 py-1 rounded border border-warn/50 text-text hover:bg-warn/20 transition-colors"
      >
        Return to admin
      </button>
    </div>
  );
}

function AppLayout() {
  const [checked, setChecked] = useState(
    () => sessionStorage.getItem(ONBOARD_FLAG) === '1',
  );
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (checked) return;
    // Don't bounce users who are already on onboarding or settings.
    if (location.pathname.startsWith('/onboarding')) return;
    if (location.pathname.startsWith('/settings')) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{
          ok: true;
          hasBot: boolean;
          completed: boolean;
        }>('/onboarding/status');
        if (cancelled) return;
        sessionStorage.setItem(ONBOARD_FLAG, '1');
        setChecked(true);
        if (!res.data.completed && !res.data.hasBot) {
          navigate('/onboarding', { replace: true });
        }
      } catch {
        // Status endpoint unavailable (e.g. old server) — don't block the app.
        sessionStorage.setItem(ONBOARD_FLAG, '1');
        setChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [checked, location.pathname, navigate]);

  return (
    <div className="flex min-h-screen bg-bg">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <ImpersonationBanner />
        <TopBar />
        {/* Single centered content column — consistent page margins app-wide.
            Pages render into this container, so nothing stretches edge-to-edge
            on wide screens. */}
        <main className="flex-1 overflow-y-auto pb-24 md:pb-0">
          <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-8 py-6 md:py-8">
            <Outlet />
          </div>
        </main>
      </div>
      <MobileNav />
      <CommandPalette />
      <SetupFab />
    </div>
  );
}

export default AppLayout;
